
<template>
  <div ref="CI_TRANSACTION_LIST">
    <section-header v-if="!hideTitle"
                    ref="TRANSACTION_CAISSE"
                    :ressource="ressource"
                    :is-extended="extended"
                    :dialog="dialog"
                    :fiche=fiche
                    @extended=setExtended
                    @close-dialog=closeSection>
    </section-header>

    <v-row class="py-10 m-0">
      <v-col>
        <v-combobox ref="COMBO_BOX"
                    v-model=periodeSelected
                    class="edit-field"
                    :items="periodeList"
                    :label=labelPeriode
                    :placeholder="placeholder"
                    :persistent-placeholder="placeholder!=''"
                    :outlined="false"
                    :ripple="false">
        </v-combobox>
      </v-col>
      <v-col>
        <v-combobox ref="CAISSE_SELECT"
                    v-model=caisseSelected
                    class="edit-field"
                    :items="caisseList"
                    :label=labelCaisse
                    :placeholder="placeholder"
                    :persistent-placeholder="true"
                    :readonly="false"
                    :outlined="false"
                    :ripple="false">
        </v-combobox>
      </v-col>
      <v-col>
        <v-combobox ref="ETABLISSEMENT_SELECT"
                    v-model=etablissementSelected
                    class="edit-field"
                    :items="etablissementList"
                    :label=labelEtablissement
                    :placeholder="placeholder"
                    :persistent-placeholder="placeholder!=''"
                    :outlined="false"
                    :ripple="false">
        </v-combobox>
      </v-col>
      <v-col>
        <div class="d-flex flex-row-reverse">
          <loader-button class="ms-2"
                         label="Exporter"
                         :loading="exportLoading"
                         :disabled="!exportDisabled"
                         @callback="exportTransactions">
          </loader-button>
          <loader-button class='ms-2'
                         type='button'
                         :label='generateLabel'
                         :loading="generateLoading"
                         :disabled='generateDisabled'
                         @callback="generate">
          </loader-button>
          <loader-button class="ms-2"
                         label="Recherche"
                         :loading="searchLoading"
                         :disabled="!searchDisabled"
                         @callback="search">
          </loader-button>
        </div>
      </v-col>
    </v-row>

    <div v-if="extended">
      <v-data-table v-model="selectedItems"
                    :height="listHeight"
                    dense fixed-header
                    :headers="headers"
                    :items="items"
                    :items-per-page="-1"
                    :must-sort="true"
                    :mobile-breakpoint="100"
                    item-key="transactionId"
                    class="white"
                    no-data-text="Aucune donnée"
                    :disable-pagination="true"
                    :hide-default-footer="true"
                    :show-select="true"
                    return-object
                    @toggle-select-all="selectAll">
        <template #[`item.data-table-select`]="props">
          <v-checkbox :model-value="isSelected(props.item)"
                      :readonly="getDisabled(props.item)"
                      :disabled="getDisabled(props.item)"
                      @change="(event: any) => handleSelect(event.target.checked, props.item)"></v-checkbox>
        </template>
        <template #[`item.dateDelivery`]="{ item }">
          {{ formatDate(item.dateDelivery) }}
        </template>
        <template #[`item.dateProcess`]="{ item }">
          {{ formatDate(item.dateProcess) }}
        </template>
        <template #[`item.montant`]="{ item }">
          <v-row class="m-0">
            <v-col class="px-0">
              {{ formatCurrency(item.montant) }}
            </v-col>
            <v-col v-if="item.detail.length > 0" cols="1" class="px-0 cursor-pointer" style="justify-items: right;"
                   @click="selectDetail(item)">
              <div class="text-center align-content-center bg-information50"
                   style="height: 20px; width: 20px; border-radius: 50%;">
                <div class="text-white" style="font-size: 12px;">{{ item.detail.length }}</div>
              </div>
            </v-col>
          </v-row>
        </template>
      </v-data-table>
    </div>

    <v-row class="mt-5">
      <v-col>
        <span class="me-10">Nombre de cotisations sélectionnées: {{ nbCotis }}</span>
        <span>Montant sélectionné : {{ montantTotal }}</span>
      </v-col>
    </v-row>

    <v-dialog v-if="detailOpen" v-model="detailOpen" max-width="500">
      <v-card>
        <div class="section-titre p-4">
          Detail des montant
        </div>
        <v-card-text class="p-4 pt-0">
          <div class="pb-4 text-left">
            <div v-for="(item,index) in detailMontantList" :key="index">
              {{ formatDate(item.periode) }} - {{ formatCurrency(item.montant) }}
            </div>
          </div>
          <v-row class="m-0 d-flex justify-end">
            <div class="px-2">
              <loader-button :label="closeBtnTxt" @callback="closeDetailMontant"></loader-button>
            </div>
          </v-row>
        </v-card-text>
      </v-card>
    </v-dialog>

  </div>
</template>

<script lang="ts">
  import { GenSection, RessourceGenSection } from '@/generated/ressources/generatedRessources';
  import { Fiches, Sections } from '@/generated/classes/AppSchema';
  import TransactionList, {
    TransactionCaisse,
    TransactionCaisseDetail,
    TransactionCaisseQuery, TransactionSalarie
  } from '@/generated/classes/TransactionList';
  import { Extended } from '@/framework/base/classes/Schema';
  import Caisse, { CaisseTypes } from '@/generated/classes/Caisse';
  import ListData, { ListFilter } from '@/generated/classes/ListData';
  import Item from '@/framework/base/classes/Item';
  import SectionHeader from '@/framework/base/components/SectionHeader.vue';
  import LoaderButton from '@/framework/base/components/LoaderButton.vue';
  import Etablissement from '@/generated/classes/Etablissement';
  import { DateFormat } from '@/framework/base/helpers/DateFormat';
  import { getCurrency } from '@/framework/base/helpers/Localization';
  import { RessourceFwkButton, FwkButtons } from '@/ressources/globalRessources';
  import CiPeriod from '@/extend/classes/CiPeriod';
  import { ObjTypes } from '@/generated/classes/ObjTypes';
  import { defineComponent, type PropType } from "vue";

  export default defineComponent({
    name: 'SectionTransactionCaisse',
    components: {
      SectionHeader,
      LoaderButton
    },
    props: {
      hideTitle: {
        type: Boolean,
        required: false,
      },
      extended: {
        type: Boolean,
        required: false,
      },
      dialog: {
        type: Boolean,
        required: false,
      },
      fiche: {
        type: String,
        required: true,
      },
    },
    data() {
      return {
        listHeight: 'calc(100vh - 500px)',
        searchLoading: false,
        exportLoading: false,
        generateLoading: false,
        sectionName: Sections.TRANSACTION_CAISSE,
        placeholder: 'Sélectionner',
        selectedItems: [] as TransactionCaisse[],
        transactionList: [] as TransactionCaisse[],
        ci98TransactionQuery: new TransactionCaisseQuery() as TransactionCaisseQuery,
        caisses: [] as Caisse[],
        caisseId: new Item(-1, 'Toutes'),
        caisseList: [] as Item[],
        labelCaisse: 'Caisse',
        etablissementId: new Item(0, 'Tous'),
        etablissementList: [] as Item[],
        labelEtablissement: 'Code employeur',
        periodeId: new Item(-1, 'Toutes'),
        periodeList: [] as Item[],
        labelPeriode: 'Période',
        detailOpen: false,
        detailMontantList: [] as TransactionCaisseDetail[],
        closeBtnTxt: RessourceFwkButton(FwkButtons.FERMER).label,
      };
    },
    computed: {
      ressource(): any {
        return RessourceGenSection(GenSection.TRANSACTION_CAISSE);
      },
      generateLabel(): any {
        return RessourceFwkButton(FwkButtons.GENERER_VIREMENT).label;
      },
      headers(): any {
        return [
          { title: "Code", value: 'etablissementId', cellClass: '', sortable: true },
          { title: "Etablissement", value: 'raisonSociale', cellClass: '', sortable: true },
          { title: "SIRET", value: 'siret', cellClass: '', sortable: true },
          { title: "Caisse", value: 'nomCaisse', cellClass: '', sortable: true },
          { title: "URSSAF", value: 'codeUrssaf', cellClass: '', sortable: true, align: 'left' },
          { title: "Période", value: 'periode', cellClass: '', sortable: true },
          { title: "Montant", value: 'montant', cellClass: '', sortable: true, align: 'left' },
          { title: "N° chèque", value: 'deliveryId', cellClass: '', sortable: true, align: 'left' },
          { title: "Paiement", value: 'dateDelivery', cellClass: '', sortable: true, align: 'center' },
          { title: "Encaissement", value: 'datePayed', cellClass: '', sortable: true, align: 'center' }
        ];
      },
      nbCotis(): Number {
        return this.selectedItems.length;
      },
      montantTotal(): string {
        let sum = 0;
        this.selectedItems.forEach((item) => {
          sum += item.montant;
        })
        return getCurrency(sum);
      },
      items(): TransactionCaisse[] {
        return this.transactionList;
      },
      searchDisabled(): boolean {
        return ((this.caisseId.key > 0 && (this.periodeId.key > 0 || this.etablissementId.key > 0)) || this.periodeId.key > 0 || this.etablissementId.key > 0)
      },
      generateDisabled(): boolean {
        return !(this.selectedItems.length > 0 && (this.periodeId.key > 0 || this.caisseId.key > 0));
      },
      exportDisabled(): boolean {
        return this.selectedItems.length > 0;
      },
      caisseSelected: {
        get(): Item {
          return this.caisseId;
        },
        set(item: Item) {
          this.caisseId = item;
        },
      },
      etablissementSelected: {
        get(): Item {
          return this.etablissementId;
        },
        set(item: Item) {
          this.etablissementId = item;
        },
      },
      periodeSelected: {
        get(): Item {
          return this.periodeId;
        },
        set(item: Item) {
          this.periodeId = item;
        },
      },
    },
    mounted() {
      TransactionList.checkResources();
      Caisse.list(new ListFilter).then((listData) => {
        const res: Item[] = [];
        res.push(new Item(-1, 'Toutes'));
        const records = listData.reccords as Caisse[];
        records.forEach((caisse) => {
          if (caisse.type == CaisseTypes.NONE && !caisse.nom.includes('Neant') && !caisse.nom.includes('AUDIENS -')) {
            res.push(new Item(caisse.id, caisse.nom));
            this.caisses.push(caisse);
          }
        })
        this.caisseList = res;
      })

      Etablissement.listClient(new ListFilter()).then((etablissements: Etablissement[]) => {
        const res: Item[] = [];
        res.push(new Item(0, 'Tous'));
        etablissements.forEach((etablissement) => {
          if (etablissement.clientId > 0) {
            let label = etablissement.id.toString() + " - ";
            label += etablissement.alias != "" ? etablissement.alias : etablissement.nom;
            res.push(new Item(etablissement.id, label));
          }
        })
        this.etablissementList = res;
      })

      let lastPeriod = -1;
      CiPeriod.list(new ListFilter()).then((listData: ListData) => {
        const res: Item[] = [];
        res.push(new Item(-1, 'Toutes'));
        const ciPeriods = listData.reccords as CiPeriod[];
        ciPeriods.sort((a, b) => b.id - a.id);
        ciPeriods.forEach((ciPeriod) => {
          const label = ciPeriod.id.toString() + " - " + ciPeriod.label;
          if (lastPeriod == -1) {
            lastPeriod = ciPeriod.id;
            this.periodeId = new Item(ciPeriod.id, label);
          }
          res.push(new Item(ciPeriod.id, label));
        })
        this.periodeList = res;

        // Initial load with last period
        const query = new TransactionCaisseQuery();
        query.periodeId = lastPeriod;
        query.etablissementId = 0;
        query.caisseId = -1;
        this.loadTable(query);
      })
    },
    methods: {
      loadTable(query: TransactionCaisseQuery): void {
        this.searchLoading = true;
        this.transactionList = [];
        this.selectedItems = [];
        this.nbCotis = 0;
        TransactionList.listTransactionsCaisse(query).then((res) => {
          this.nbCotis = res.length;
          let totalCotis = 0;
          res.forEach((cotis) => {
            if (cotis.montant) {
              totalCotis += cotis.montant;
            }
          });
          this.transactionList = res;
          this.searchLoading = false;
          this.selected(res);
        }).catch(() => {
          this.searchLoading = false;
        });
      },
      getDisabled(item: TransactionCaisse): boolean {
        return !((item.deliveryId == '') && item.periode != '' && item.etablissementId > 0 && item.montant > 0);
      },
      handleSelect(isSelected: boolean, item: TransactionCaisse) {
        const selectedItems = this.selectedItems;
        if (isSelected && !selectedItems.includes(item)) {
          selectedItems.push(item);
        } else {
          if (!isSelected && selectedItems.includes(item)) {
            const index = selectedItems.indexOf(item);
            selectedItems.splice(index, 1);
          }
        }
      },
      isSelected(item: TransactionCaisse): boolean {
        return !!this.selectedItems.find(a => a.transactionId == item.transactionId);
      },
      selectAll(transactions: TransactionCaisse[]) {
        if (this.selectedItems.length > 0) {
          this.selectedItems = [];
        }
      },
      selected(ci98Transactions: TransactionCaisse[]) {
        this.selectedItems = [];
        ci98Transactions.forEach((ci98Transaction) => {
          if (!this.getDisabled(ci98Transaction)) {
            this.selectedItems.push(ci98Transaction);
          }
        })
      },
      formatDate(value: Date): string {
        return DateFormat.getDateLabel(value);
      },
      formatCurrency(value: number): string {
        return getCurrency(value);
      },
      search(): void {
        const query = new TransactionCaisseQuery();
        query.periodeId = this.periodeId.key;
        query.etablissementId = this.etablissementId.key;
        query.caisseId = this.caisseId.key;
        this.loadTable(query);
      },
      generate(): void {
        this.generateLoading = true;
        const ids: number[] = [];
        this.selectedItems.forEach((item) => {
          ids.push(item.transactionId);
        })
        const deliverList = new TransactionList
        deliverList.objType = ObjTypes.CAISSE;
        deliverList.ids = ids;
        TransactionList.deliverTransactions(deliverList).then(() => {
          this.generateLoading = false;
          this.selectedItems = [];
          this.search();
        })
      },
      exportTransactions(): void {
        // to implement
      },
      setExtended(extended: Extended): void {
        this.$emit('extended', extended);
      },
      closeSection(): void {
        this.$emit('closeDialog');
      },
      selectDetail(ci98Transaction: TransactionCaisse) {
        this.detailOpen = true;
        this.detailMontantList = [];
        ci98Transaction.detail.forEach((item) => {
          this.detailMontantList.push(item);
        })
      },
      closeDetailMontant(): void {
        this.detailOpen = false;
      },
    },
  });
</script>
